import {getYeastPercentFromId, getYeastTotalTspFromId, yeastDefs} from "../shared/Yeast";
import {toFixedDecimal} from "../utils/NumberUtils";
import {convertGramsToUnits, unitFpUnit, unitTeaspoon} from "../shared/Units";
import {getEffectiveLiquidPct, getNormalizedFlourName, getNormalizedInclusionName} from "../shared/IngredientSupport";
import {toCapitalizedWords} from "../utils/StringUtils";
import {
  ingredientTypeGrain,
  ingredientTypeInclusion,
  ingredientTypeLiquid,
  ingredientTypeTopping
} from "../shared/Types";
import {sortOnStringField} from "../utils/ArrayUtils";

export function levainTwToPff(doughFLourWeight, starterPctF, starterHydrationPctF) {
  if (!doughFLourWeight) {
    return 0
  }
  const levainFlourWeight = (doughFLourWeight * starterPctF) / (1 + starterHydrationPctF)
  return levainFlourWeight / (doughFLourWeight + levainFlourWeight )
}

export function levainPffToTw(flourWeight, levainWeight) {
  return Math.round(100 * (levainWeight / flourWeight))
}

export function levainPffToTwEstimate(pffPct, levainHydrationPct, levainSeedPct) {
  const pffPctF = pffPct / 100
  const levainHydrationPctF = levainHydrationPct / 100
  // don't incorporate the seed% since flour calculations include starter seed
  return Math.round((pffPctF + (pffPctF * levainHydrationPctF)) * 100)
}

export function calculateYeastForRatio(flourWeight, yeastRatioId, yeastPct) {
  const yeastPercent = yeastPct || getYeastPercentFromId(yeastRatioId)
  const altUnitAmt = toFixedDecimal(getYeastTotalTspFromId(yeastRatioId, flourWeight), 3)
  return {
    ratioId: yeastRatioId,
    weight: toFixedDecimal(yeastPercent * (flourWeight / yeastDefs.flourUnitGrams), 1),
    percent: toFixedDecimal(yeastPercent),
    altUnit: unitTeaspoon,
    altUnitAmount: altUnitAmt,
    volumeDisplayLabel: `${altUnitAmt} Tsp`
  }
}

// total preferment flour: reduce by liquid (total H%) and mixin pct sum, if any, which isn't common
export function calculateLevainFlourByTotalWeight(levainTotalWeight, yeastPctFraction, hydrationPctFraction, mixinInput) {
  let mixinRatioSum = 0
  mixinInput?.forEach((md) => {
    mixinRatioSum += (md.percent / 100)
  })
  return Math.round(levainTotalWeight / (1 + yeastPctFraction + hydrationPctFraction + mixinRatioSum))
}

export function getPrefermentComponents(totalFlourWt, seedPct, seedHydroPct, totalHydroPct) {
  // Note: the new flour calculation could be more detailed if we drill deeper into the makeup of the seed itself:
  //    (totalFlourWt / (1 + (seedPct / (1 + seedHydroPct + motherSeedPct))))

  const newFlourWt = Math.round(totalFlourWt / (1 + (seedPct / (1 + seedHydroPct))));

  const seedWeight = Math.round(newFlourWt * seedPct)
  const seedWtDist = getWeightDistribution(seedWeight, seedHydroPct)
  const seedFlourWt = seedWtDist.flourWeight
  const seedLiquidWt = seedWtDist.liquidWeight
  const totalLiquidWt = Math.round(totalFlourWt * totalHydroPct)
  const newLiquidWt = totalLiquidWt - seedLiquidWt

  return {
    totalFlourWt: totalFlourWt,
    seedPct: seedPct,
    seedHydroPct: seedHydroPct,
    totalHydroPct: totalHydroPct,
    seedWeight: seedWeight,
    seedFlourWt: seedFlourWt,
    seedLiquidWt: seedLiquidWt,
    totalLiquidWt: totalLiquidWt,
    newFlourWt: newFlourWt,
    newLiquidWt: newLiquidWt,
    totalWt: (seedWeight + newFlourWt + newLiquidWt)
  }
}

export function getPrefermentRefreshBuildComponents(totalTargetWeight, yeastPct, starterPct, starterHydrationPct, hydrationPct, mixinInput) {
  const refresh1 = {}
  const refresh2 = {}

  const refresh2EffFlourWeight = calculateLevainFlourByTotalWeight(totalTargetWeight, yeastPct, hydrationPct, mixinInput)
  const refresh2Comp = getPrefermentComponents(refresh2EffFlourWeight, 1.0, hydrationPct, hydrationPct)
  refresh2['flourWeight']   = refresh2Comp.newFlourWt
  refresh2['liquidWeight']  = refresh2Comp.newLiquidWt
  refresh2['starterWeight'] = refresh2Comp.seedWeight
  refresh2['totalWeight'] = refresh2Comp.totalWt


  const refresh1EffFlourWeight = calculateLevainFlourByTotalWeight(refresh2Comp.seedWeight, yeastPct, hydrationPct, mixinInput)
  const refresh1Comp = getPrefermentComponents(refresh1EffFlourWeight, starterPct, starterHydrationPct, hydrationPct)
  refresh1['flourWeight']   = refresh1Comp.newFlourWt
  refresh1['liquidWeight']  = refresh1Comp.newLiquidWt
  refresh1['starterWeight'] = refresh1Comp.seedWeight
  refresh1['totalWeight'] = refresh1Comp.totalWt

  const seedAdjust = refresh2.starterWeight - refresh1.totalWeight
  // adjust for rounding by adding delta to refresh1's liquid so refresh2 seed amount lines up
  refresh1.liquidWeight += seedAdjust
  refresh1.totalWeight += seedAdjust
  const finalAdjust = totalTargetWeight - refresh2.totalWeight
  // adjust for rounding by adding delta to refresh1's liquid so refresh2 seed amount lines up
  refresh2.liquidWeight += finalAdjust
  refresh2.totalWeight += finalAdjust

  // console.log(`refresh1: ${JSON.stringify(refresh1)}`)
  // console.log(`refresh2: ${JSON.stringify(refresh2)}`)
  return {refresh1: refresh1, refresh2: refresh2}
}

export function getWeightDistribution(doughWeight, hydrationPctFraction) {
  const flourWeight = Math.round(doughWeight / (1 + hydrationPctFraction))
  const liquidWeight = doughWeight - flourWeight
  return {flourWeight: flourWeight, liquidWeight: liquidWeight}
}

export function getPercentDistribution(doughPercentFraction, hydrationPctFraction) {
  const flourPctFraction = (doughPercentFraction / (1 + hydrationPctFraction))
  const liquidPctFraction = doughPercentFraction - flourPctFraction
  return {flourPctFraction: flourPctFraction, liquidPctFraction: liquidPctFraction}
}

//
// Ingredient summaries
//  These summaries are only used for recipe ingredient and cost summarization and
//  do not influence H% - in particular this may include some submix liquids that
//  are technically counted as ingredients but may not count toward H%
//
export function getGrainSummary(items) {
  // use actual new flour weight as the basis for percentages
  const recipeFlourTotalWeight = items.recipeFlourTotalWeight
  const grainSummaryItems = []
  const allGrains = [...items.mainDough.flour]
  if (items.prefermentDough?.flour?.length > 0) {
    allGrains.push.apply(allGrains, items.prefermentDough.flour)
  }
  items.mainDough.submixes?.forEach(sm => {
    if (sm.flour?.length > 0) {
      allGrains.push.apply(allGrains, sm.flour)
    }
    if (sm.grain?.length > 0) {
      allGrains.push.apply(allGrains, sm.grain)
    }
  })
  for (let grain of allGrains) {
    const flourName = getNormalizedFlourName(toCapitalizedWords(grain['name'].trim()))
    let grainItem = grainSummaryItems.find(gr => gr['name'] === flourName)
    if (!grainItem) {
      grainSummaryItems.push({type: ingredientTypeGrain, name: flourName, percent: 0, weight: grain['weight'] ?? 0})
    } else {
      grainItem['weight'] += grain['weight']
    }
  }
  for (const grainSummary of grainSummaryItems) {
    let ingredientPct = 0
    if (recipeFlourTotalWeight > 0) {
      ingredientPct = toFixedDecimal(grainSummary['weight'] / recipeFlourTotalWeight * 100)
    }
    grainSummary['percent'] = ingredientPct
  }
  if (recipeFlourTotalWeight > 0) {
    grainSummaryItems.sort((a, b) => b.percent - a.percent)
  } else { //name sort
    grainSummaryItems.sort((a, b) => {return a.name < b.name ? -1 : a.name > b.name ? -1 : 0})
  }
  return grainSummaryItems
}

export function getLiquidSummary(items) {
  // use effective total flour weight as the basis for percentages
  const recipeFlourTotalWeight = items.effectiveRecipeFlourTotalWeight
  const allLiquids = [...items.mainDough.liquid]
  const liquidSummaryItems = []

  // submix liquids - include all submixes in this context: ingredients summarization w/o affecting H%
  items.mainDough.submixes?.forEach(sm => {
    if (sm.liquid?.length > 0) {
      allLiquids.push.apply(allLiquids, sm.liquid)
    }
  })
  // preferment liquids
  if (items.prefermentDough?.liquid?.length > 0) {
    allLiquids.push.apply(allLiquids, items.prefermentDough.liquid)
  }
  for (let liquid of allLiquids) {
    const liquidName = getNormalizedInclusionName(toCapitalizedWords(liquid['name'].trim()))
    let liquidItem = liquidSummaryItems.find(li => li['name'] === liquidName)
    if (!liquidItem) {
      liquidSummaryItems.push({type: ingredientTypeLiquid, name: liquidName, percent: 0, weight: liquid['weight'] ?? 0})
    } else {
      liquidItem['weight'] += liquid['weight']
    }
  }
  for (const liquidSummary of liquidSummaryItems) {
    let ingredientPct = 0
    if (recipeFlourTotalWeight > 0) {
      ingredientPct = toFixedDecimal(liquidSummary['weight'] / recipeFlourTotalWeight * 100)
    }
    liquidSummary['percent'] = ingredientPct
  }
  if (recipeFlourTotalWeight > 0) {
    liquidSummaryItems.sort((a, b) => b.percent - a.percent)
  } else { //name sort
    liquidSummaryItems.sort((a, b) => {return a.name < b.name ? -1 : a.name > b.name ? -1 : 0})
  }
  return liquidSummaryItems
}

export function getInclusionSummary(items) {
  // use effective total flour weight as the basis for percentages
  const recipeFlourTotalWeight = items.effectiveRecipeFlourTotalWeight
  const inclusionSummaryItems = []
  const allInclusions = [...items.mainDough.mixin]

  // submix inclusions
  items.mainDough.submixes?.forEach(sm => {
    if (sm.dry?.length > 0) {
      allInclusions.push.apply(allInclusions, sm.dry)
    }
  })
  // preferment inclusions
  if (items.prefermentDough?.mixin?.length > 0) {
    allInclusions.push.apply(allInclusions, items.prefermentDough.mixin)
  }

  for (let inclusion of allInclusions) {
    let inclusionName = getNormalizedInclusionName(toCapitalizedWords(inclusion['name'].trim()))
    let inclusionItem = inclusionSummaryItems.find(inc => inc['name'] === inclusionName)
    if (!inclusionItem) {
      inclusionSummaryItems.push({type: ingredientTypeInclusion, name: inclusionName, weight: inclusion['weight'] ?? 0})
    } else {
      inclusionItem['weight'] += inclusion['weight']
    }
  }
  for (const ingredientSummary of inclusionSummaryItems) {
    // now set the percent...
    const effLiquidPct = getEffectiveLiquidPct(ingredientSummary['name'])
    if (effLiquidPct) {
      ingredientSummary['effLiquidPct'] = effLiquidPct
    }
    let ingredientPct = 0
    if (recipeFlourTotalWeight > 0) {
      ingredientPct = toFixedDecimal(ingredientSummary['weight'] / recipeFlourTotalWeight * 100)
    }
    ingredientSummary['percent'] = ingredientPct
  }
  if (recipeFlourTotalWeight > 0) {
    inclusionSummaryItems.sort((a, b) => b.percent - a.percent)
  } else { //name sort
    inclusionSummaryItems.sort((a, b) => {return a.name < b.name ? -1 : a.name > b.name ? -1 : 0})
  }
  return inclusionSummaryItems
}

export function getAllIngredientSummary(items) {
  const grainSummaries = getGrainSummary(items)
  const liquidSummaries = getLiquidSummary(items)
  const inclusionSummaries = getInclusionSummary(items)
  return {grain: grainSummaries, liquid: liquidSummaries, inclusion: inclusionSummaries}
}

export function calculateIngredientCosts(ingredientSummaries, costData, numRecipeUnits) {
  const ingredientCosts = []
  const allSummaries = [...ingredientSummaries.grain, ...ingredientSummaries.inclusion, ...ingredientSummaries.liquid]
  const grainCd = costData.filter((cd) => cd['priceAmount'] > 0 && cd['type'] === ingredientTypeGrain).sort((a,b) => sortOnStringField(a,b,'name'))
  const inclusionCd = costData.filter((cd) => cd['priceAmount'] > 0 && cd['type'] === ingredientTypeInclusion).sort((a,b) => sortOnStringField(a,b,'name'))
  const liquidCd = costData.filter((cd) => cd['priceAmount'] > 0 && cd['type'] === ingredientTypeLiquid).sort((a,b) => sortOnStringField(a,b,'name'))
  const toppingCd = costData.filter((cd) => cd['priceAmount'] > 0 && cd['type'] === ingredientTypeTopping).sort((a,b) => sortOnStringField(a,b,'name'))
  const allCostData = [...grainCd, ...inclusionCd, ...liquidCd]
  allCostData.forEach(cd => {
    const summaryItem = allSummaries.find(gs => gs['name'] === cd['name'] && gs['type'] === cd['type'])
    if (summaryItem) {
      // convert weight in grams to the target unit weight, then multiply that by the target unit price
      const targetUnitWeight = convertGramsToUnits(summaryItem['weight'], cd['priceUnit'], cd['type'])
      const costTotal = toFixedDecimal((targetUnitWeight * cd['priceAmount']), 2)
      const costPerUnit = toFixedDecimal(costTotal / numRecipeUnits, 2)
      const ingredientCost = {...cd, costTotal: costTotal, costPerUnit: costPerUnit}
      ingredientCosts.push(ingredientCost)
    }
  })
  // handle toppings differently: as fixed total recipe cost since form doesn't calc weights from percentages
  toppingCd.forEach(cd => {
    let costTotal
    let costPerUnit
    if (cd['priceUnit'] === unitFpUnit) {
      costPerUnit = toFixedDecimal(cd['priceAmount'], 2)
      costTotal = toFixedDecimal(costPerUnit * numRecipeUnits)
    } else {
      costTotal = toFixedDecimal(cd['priceAmount'], 2)
      costPerUnit = toFixedDecimal(costTotal / numRecipeUnits)
    }
    const ingredientCost = {...cd, costTotal: costTotal, costPerUnit: costPerUnit}
    ingredientCosts.push(ingredientCost)
  })
  return ingredientCosts
}
